import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import {
  PLANOS_FIBRA,
  PLANOS_MOVEL_CNPJ,
  PLANOS_PORTABILIDADE_CNPJ,
  PLANOS_RENOVACAO_CNPJ,
  PLANOS_TV,
  type ComposicaoProposta,
  type LinhaComposicao,
  type PlanoCNPJ,
  type TipoLinha,
} from '@/constants/planosCNPJ';

type ItemPedido = {
  id: string;
  linha_id: string | null;
  ddd: string | null;
  linha: string | null;
  linhas_telefone: unknown;
  plano_nome: string | null;
  valor_unit: number | string | null;
  franquia_gb: number | null;
  quantidade: number | string | null;
  produto_id: string;
};

type ConfiguracaoProduto = {
  tipo: TipoLinha;
  categoria: string;
  tipoBloco?: 'NOVO' | 'INCREMENTO' | 'RENOVACAO';
};

function configuracaoProduto(nome: string): ConfiguracaoProduto | null {
  const normalizado = nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  if (normalizado === 'TT POS-PJ') return { tipo: 'TT', categoria: 'tt_pos' };
  if (normalizado === 'TT PRE-PJ') return { tipo: 'TT_PRE', categoria: 'tt_pre' };
  if (normalizado === 'PN INCREMENTO') return { tipo: 'PORTABILIDADE', categoria: 'pn_incremento' };
  if (normalizado === 'PN NOVO') return { tipo: 'PORTABILIDADE', categoria: 'pn_novo' };
  if (normalizado === 'HA INCREMENTO') return { tipo: 'NOVA', categoria: 'bloco_incr', tipoBloco: 'INCREMENTO' };
  if (normalizado === 'HA NOVO') return { tipo: 'LINHA_NOVA', categoria: 'nova' };
  if (normalizado === 'RENOVACAO') return { tipo: 'RENOVACAO', categoria: 'renov' };
  if (normalizado.includes('FIBRA')) return { tipo: 'FIBRA', categoria: 'fibra' };
  if (normalizado.includes('TV')) return { tipo: 'TV', categoria: 'tv' };
  return null;
}

function numeroDoItem(item: ItemPedido): string {
  const direto = `${item.ddd ?? ''}${item.linha ?? ''}`.replace(/\D/g, '');
  if (direto.length >= 10) return direto;
  if (!Array.isArray(item.linhas_telefone)) return '';
  const primeira = item.linhas_telefone[0] as Record<string, unknown> | undefined;
  if (!primeira) return '';
  return `${primeira.ddd ?? ''}${primeira.linha ?? primeira.numero ?? ''}`.replace(/\D/g, '');
}

function planosDaConfiguracao(configuracao: ConfiguracaoProduto): PlanoCNPJ[] {
  if (configuracao.tipo === 'PORTABILIDADE') return PLANOS_PORTABILIDADE_CNPJ;
  if (configuracao.tipo === 'RENOVACAO') return PLANOS_RENOVACAO_CNPJ;
  if (configuracao.tipo === 'FIBRA') return PLANOS_FIBRA;
  if (configuracao.tipo === 'TV') return PLANOS_TV;
  return PLANOS_MOVEL_CNPJ;
}

function planoMaisProximo(item: ItemPedido, configuracao: ConfiguracaoProduto) {
  const valor = Number(item.valor_unit ?? 0);
  const gb = Number(item.franquia_gb ?? 0);
  return planosDaConfiguracao(configuracao)
    .slice()
    .sort((a, b) => {
      const valorA = Math.abs(Number(a.valor) - valor);
      const valorB = Math.abs(Number(b.valor) - valor);
      if (valorA !== valorB) return valorA - valorB;
      return Math.abs(Number(a.gb ?? 0) - gb) - Math.abs(Number(b.gb ?? 0) - gb);
    })[0] ?? null;
}

export async function prepararPropostaRevisaoIA(pedidoId: string, leadId: string): Promise<void> {
  const { data: lead, error: erroLead } = await supabase
    .from('leads_carteira')
    .select('proposta_composicao_cnpj')
    .eq('id', leadId)
    .maybeSingle();
  if (erroLead) throw erroLead;

  const composicaoAtual = lead?.proposta_composicao_cnpj as { linhas?: unknown[] } | null;
  if (Array.isArray(composicaoAtual?.linhas) && composicaoAtual.linhas.length > 0) return;

  const { data: pedido, error: erroPedido } = await supabase
    .from('pedidos')
    .select('id, pedido_mae_id, descricao, origem_lead')
    .eq('id', pedidoId)
    .maybeSingle();
  if (erroPedido) throw erroPedido;
  if (!pedido) throw new Error('Pedido não encontrado para revisão.');

  const pedidoIA = pedido.origem_lead === 'AGENTE_IA' || pedido.descricao?.startsWith('Pedido fechado via IA');
  const pedidoRaizId = pedido.pedido_mae_id ?? pedido.id;

  if (!pedidoIA && pedido.pedido_mae_id) {
    const { data: pedidoMae, error: erroMae } = await supabase
      .from('pedidos')
      .select('descricao, origem_lead')
      .eq('id', pedido.pedido_mae_id)
      .maybeSingle();
    if (erroMae) throw erroMae;
    if (pedidoMae?.origem_lead !== 'AGENTE_IA' && !pedidoMae?.descricao?.startsWith('Pedido fechado via IA')) return;
  } else if (!pedidoIA) {
    return;
  }

  const { data: filhos, error: erroFilhos } = await supabase
    .from('pedidos')
    .select('id')
    .eq('pedido_mae_id', pedidoRaizId);
  if (erroFilhos) throw erroFilhos;
  const pedidoIds = [pedidoRaizId, ...((filhos ?? []).map((filho) => filho.id))];

  const { data: itensData, error: erroItens } = await supabase
    .from('itens_pedido')
    .select('id, linha_id, ddd, linha, linhas_telefone, plano_nome, valor_unit, franquia_gb, quantidade, produto_id')
    .in('pedido_id', pedidoIds);
  if (erroItens) throw erroItens;
  const itens = (itensData ?? []) as ItemPedido[];
  if (itens.length === 0) throw new Error('Este pedido não possui itens para carregar na proposta.');

  const produtoIds = [...new Set(itens.map((item) => item.produto_id))];
  const { data: produtosData, error: erroProdutos } = await supabase
    .from('produtos')
    .select('id, nome')
    .in('id', produtoIds);
  if (erroProdutos) throw erroProdutos;
  const nomesProdutos = new Map((produtosData ?? []).map((produto) => [produto.id, produto.nome]));
  const configuracoes = itens.map((item) => configuracaoProduto(nomesProdutos.get(item.produto_id) ?? ''));
  if (configuracoes.some((configuracao) => !configuracao)) {
    throw new Error('O pedido possui produto que não pode ser carregado automaticamente na proposta.');
  }

  const linhas: LinhaComposicao[] = itens.map((item, indice) => {
    const configuracao = configuracoes[indice]!;
    const plano = planoMaisProximo(item, configuracao);
    const linha: LinhaComposicao = {
      id: crypto.randomUUID(),
      linha_id: item.linha_id ?? crypto.randomUUID(),
      numero: numeroDoItem(item),
      tipo: configuracao.tipo,
      planoId: plano?.id ?? null,
      plano: plano ? {
        ...plano,
        nome: plano.nome,
        gb: Number(plano.gb ?? item.franquia_gb ?? 0),
        valor: Number(plano.valor),
        categoria: configuracao.categoria,
      } : undefined,
      adicionais: {},
      tipoBloco: configuracao.tipoBloco,
    };
    if (configuracao.categoria === 'pn_incremento') linha.categoriaCustom = configuracao.categoria;
    return linha;
  });

  const composicao: ComposicaoProposta = {
    linhas,
    incluiFibra: false,
    totalGB: linhas.reduce((total, linha) => total + Number(linha.plano?.gb ?? 0), 0),
    totalValor: linhas.reduce((total, linha) => total + Number(linha.plano?.valor ?? 0), 0),
    economiaValor: 0,
    economiaPercent: 0,
  };

  const { error: erroSalvar } = await supabase
    .from('leads_carteira')
    .update({ proposta_composicao_cnpj: composicao as unknown as Json })
    .eq('id', leadId);
  if (erroSalvar) throw erroSalvar;
}
